// Donor-scope per-org detail API helper — CHG-1517 wave 2,
// CHG-2947 (Slice F, DP-R16) org-updates envelope fix.
//
// Backend: portal.py canonical GET /api/v1/my-giving/org/{org_id}
// → PortalOrgDetail. Sibling endpoints on the same prefix:
//   GET /my-giving/org/{org_id}/updates    (recent broadcast messages)
//   GET /my-giving/org/{org_id}/campaigns  (campaigns roster)
//   GET /my-giving/org/{org_id}/events     (upcoming + past events)
//
// Wave 2 fetches only the primary /org/{id} payload + updates list.
// Campaigns + events are exposed as helpers below for future expansion
// (deferred — keeps wave 2 page scope tight).

import { apiClient } from "./client";
import { normalizeListResponse, type RawListEnvelope } from "./list-contract";

export interface PortalOrgDetail {
  id: string;
  name: string;
  logo_url: string | null;
  slug: string | null;
  mission_statement: string | null;
  total_donated_minor_units: number;
  donation_count: number;
  active_recurring_count: number;
  open_pledges_count: number;
  currency: string;
}

// CHG-2947 (DP-R16) — mirrors the DEPLOYED _message_to_feed_item
// projection (routers/portal.py; documented in tier-04
// donor-portal.api.yaml §PortalFeedItem). The previous revision's
// `author_name` field was INVENTED — the runtime never emits it.
export interface PortalOrgUpdate {
  id: string;
  organization_id: string;
  title: string | null;
  body: string;
  image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  announcement_type: string | null;
  published_at: string | null;
  /** Always null on the org-updates variant (cross-org /feed only). */
  donor_id: string | null;
}

export async function fetchMyOrgDetail(orgId: string): Promise<PortalOrgDetail> {
  const r = await apiClient().get<PortalOrgDetail>(
    `/my-giving/org/${orgId}`,
  );
  return r.data;
}

export async function fetchMyOrgUpdates(
  orgId: string,
): Promise<PortalOrgUpdate[]> {
  // CHG-2947 (DP-R16): the route returns the donor-portal FLAT offset
  // envelope {data, page, per_page, total, total_pages}. The pre-fix
  // helper accepted only a bare array or {items}, so the MyOrgDetail
  // "Recent updates" panel ALWAYS rendered empty (its test mocked this
  // helper, hiding the mismatch). Normalize through the shared
  // list-contract flat-envelope branch; tolerate a bare array
  // defensively.
  const r = await apiClient().get<
    PortalOrgUpdate[] | RawListEnvelope<PortalOrgUpdate>
  >(`/my-giving/org/${orgId}/updates`);
  const body = r.data;
  if (Array.isArray(body)) return body;
  return normalizeListResponse<PortalOrgUpdate>(body).items;
}

export const meOrgDetailApi = {
  fetchMyOrgDetail,
  fetchMyOrgUpdates,
};
